
import { Target, TrendingUp, Calendar, CheckCircle, BarChart3 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const AppPreviewMockups = () => {
  return (
    <section className="container max-w-6xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h3 className="text-3xl font-bold text-deep-blue mb-4">
          See Show Up! in Action
        </h3>
        <p className="text-xl text-deep-blue/70 max-w-2xl mx-auto">
          Take a peek at what you'll see inside the app. Real progress, real PACTs, real results.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {/* Home Screen Mockup */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-deep-blue text-center">Your Daily Dashboard</h4>
          <div className="bg-white rounded-3xl p-6 shadow-lg border border-soft-blue/50 max-w-sm mx-auto">
            {/* Header */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-deep-blue mb-2">Show Up!</h1>
              <p className="text-deep-blue/70 text-sm">Amazing! You showed up today! 🌟</p>
            </div>

            {/* Today's Progress */}
            <Card className="border-none shadow-sm bg-soft-blue/20 mb-6">
              <CardContent className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-deep-blue font-medium">Today's Progress</span>
                  <span className="text-vibrant-orange font-bold">3/3</span>
                </div>
                <div className="w-full bg-white rounded-full h-3">
                  <div className="bg-vibrant-orange h-3 rounded-full w-full"></div>
                </div>
                <p className="text-xs text-deep-blue/70 mt-2">Perfect day! All PACTs completed ✨</p>
              </CardContent>
            </Card>

            {/* Sample PACTs */}
            <div className="space-y-3 mb-6">
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-deep-blue text-sm">Read for 10 minutes</h3>
                      <p className="text-xs text-deep-blue/70">Day 18 • 🔥 18-day streak</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-deep-blue text-sm">Exercise for 20 minutes</h3>
                      <p className="text-xs text-deep-blue/70">Day 12 • 🔥 12-day streak</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-deep-blue text-sm">Meditate for 5 minutes</h3>
                      <p className="text-xs text-deep-blue/70">Day 25 • 🔥 25-day streak</p>
                    </div>
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-lg font-bold text-vibrant-orange">3</div>
                <div className="text-xs text-deep-blue/70">Active</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-500">18</div>
                <div className="text-xs text-deep-blue/70">Best Streak</div>
              </div>
              <div>
                <div className="text-lg font-bold text-deep-blue">94%</div>
                <div className="text-xs text-deep-blue/70">Success Rate</div>
              </div>
            </div>
          </div>
        </div>

        {/* Insights Screen Mockup */}
        <div className="space-y-4">
          <h4 className="text-lg font-semibold text-deep-blue text-center">Your Progress Insights</h4>
          <div className="bg-white rounded-3xl p-6 shadow-lg border border-soft-blue/50 max-w-sm mx-auto">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-deep-blue mb-1">Insights</h1>
              <p className="text-deep-blue/70 text-sm">Deep analysis of your PACTs performance</p>
            </div>

            {/* Summary Metrics */}
            <div className="grid grid-cols-2 gap-3 mb-6">
              <Card className="border-none shadow-sm bg-white/80">
                <CardContent className="p-3 text-center">
                  <div className="w-8 h-8 bg-deep-blue/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Target className="w-4 h-4 text-deep-blue" />
                  </div>
                  <div className="text-xl font-bold text-deep-blue mb-1">12</div>
                  <div className="text-xs text-deep-blue/70">Total Created</div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm bg-white/80">
                <CardContent className="p-3 text-center">
                  <div className="w-8 h-8 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="text-xl font-bold text-deep-blue mb-1">9</div>
                  <div className="text-xs text-deep-blue/70">Completed</div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm bg-white/80">
                <CardContent className="p-3 text-center">
                  <div className="w-8 h-8 bg-vibrant-orange/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <TrendingUp className="w-4 h-4 text-vibrant-orange" />
                  </div>
                  <div className="text-xl font-bold text-deep-blue mb-1">3</div>
                  <div className="text-xs text-deep-blue/70">Active</div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-sm bg-white/80">
                <CardContent className="p-3 text-center">
                  <div className="w-8 h-8 bg-deep-blue/10 rounded-full flex items-center justify-center mx-auto mb-2">
                    <BarChart3 className="w-4 h-4 text-deep-blue" />
                  </div>
                  <div className="text-xl font-bold text-deep-blue mb-1">75%</div>
                  <div className="text-xs text-deep-blue/70">Success Rate</div>
                </CardContent>
              </Card>
            </div>

            {/* Mock Chart */}
            <Card className="border-none shadow-sm bg-white/80 mb-6">
              <CardContent className="p-4">
                <h4 className="text-sm font-semibold text-deep-blue mb-4">Progress Over Time</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-end h-20">
                    <div className="flex flex-col justify-end h-full">
                      <div className="w-4 bg-green-500 h-12 mb-1"></div>
                      <span className="text-xs text-deep-blue/70">Jan</span>
                    </div>
                    <div className="flex flex-col justify-end h-full">
                      <div className="w-4 bg-green-500 h-16 mb-1"></div>
                      <span className="text-xs text-deep-blue/70">Feb</span>
                    </div>
                    <div className="flex flex-col justify-end h-full">
                      <div className="w-4 bg-green-500 h-20 mb-1"></div>
                      <span className="text-xs text-deep-blue/70">Mar</span>
                    </div>
                    <div className="flex flex-col justify-end h-full">
                      <div className="w-4 bg-vibrant-orange h-16 mb-1"></div>
                      <span className="text-xs text-deep-blue/70">Apr</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Legend */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-deep-blue">Completed</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-vibrant-orange rounded-full"></div>
                <span className="text-deep-blue">Active</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center mt-12">
        <p className="text-deep-blue/70 mb-6 max-w-md mx-auto">
          Ready to build your own consistency journey? Join the waitlist to be notified when the app is ready.
        </p>
        <Button className="bg-deep-blue hover:bg-deep-blue/90 text-white rounded-xl px-8 py-4 text-lg font-semibold">
          Join the Waitlist
        </Button>
      </div>
    </section>
  );
};

export default AppPreviewMockups;
